import { createTanstackQueryUtils } from "@orpc/tanstack-query";
import { type ApiOutputs, recordingSessionResultSchema } from "@rosc/contracts";
import { useMutation } from "@tanstack/react-query";
import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import { referenceClient } from "@/lib/orpc/client";
import {
  abandonRecordingSessionIntent,
  acceptRecordingSessionIntent,
  prepareRecordingSessionIntent,
  readRecordingSessionIntent,
} from "@/lib/recording-session-intent";
import { getVisitScope, useVisitScope } from "@/lib/visit-queries";

const orpc = createTanstackQueryUtils(referenceClient);
export const useCreateLinkedRecordingSession = ({
  open,
}: {
  open: boolean;
}) => {
  const scope = useVisitScope();
  const account = scope ? `${scope.uid}:${scope.accountPublicId}` : null;
  const active = useRef({ scope, open });
  if (active.current.scope !== scope || active.current.open !== open)
    active.current = { scope, open };
  const lifetime = active.current;
  const mounted = useRef(false);
  const pending = useRef<{
    lifetime: typeof lifetime;
    promise: Promise<void>;
  } | null>(null);
  const [, refresh] = useState(0);
  const [state, setState] = useState<
    | ({ lifetime: typeof lifetime } & (
        | { kind: "pending" }
        | { kind: "error"; error: string }
        | { kind: "success"; result: ApiOutputs["createRecordingSession"] }
      ))
    | null
  >(null);
  const { mutateAsync, reset: resetMutation } = useMutation({
    ...orpc.createRecordingSession.mutationOptions({
      context: { expectedUserUid: scope?.uid },
    }),
    retry: false,
    gcTime: 0,
  });
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const current = () =>
    mounted.current &&
    open &&
    active.current === lifetime &&
    !!scope &&
    getVisitScope() === scope;
  let saved: ReturnType<typeof readRecordingSessionIntent> = null;
  let storageError: string | null = null;
  try {
    if (account) saved = readRecordingSessionIntent(account);
  } catch {
    storageError = t("visits.sidebar.micSync.unreadable");
  }
  const createLinkedRecordingSession = (deviceId: string): Promise<void> => {
    if (!scope || !account || !current()) return Promise.resolve();
    if (pending.current?.lifetime === lifetime) return pending.current.promise;
    setState({ lifetime, kind: "pending" });
    const promise = (async () => {
      try {
        if (storageError) throw Error(storageError);
        const identity = prepareRecordingSessionIntent(account, deviceId);
        const result = recordingSessionResultSchema.parse(
          await mutateAsync({
            headers: { "idempotency-key": identity.key },
            body: { deviceId: identity.deviceId },
          }),
        );
        if (result.id !== identity.key || result.deviceId !== identity.deviceId)
          throw Error("Mismatched microphone request");
        if (!current()) return;
        acceptRecordingSessionIntent(account, identity.key);
        if (current()) setState({ lifetime, kind: "success", result });
      } catch {
        if (current())
          setState({
            lifetime,
            kind: "error",
            error: t("visits.sidebar.micSync.failed"),
          });
      } finally {
        if (active.current === lifetime) resetMutation();
      }
    })();
    pending.current = { lifetime, promise };
    void promise.finally(() => {
      if (pending.current?.promise === promise) pending.current = null;
    });
    return promise;
  };
  const reset = () => {
    active.current = { scope, open };
    setState(null);
    resetMutation();
    refresh((value) => value + 1);
  };
  const abandon = () => {
    if (!account || !current() || pending.current?.lifetime === lifetime)
      return;
    try {
      abandonRecordingSessionIntent(account);
      reset();
    } catch {
      setState({
        lifetime,
        kind: "error",
        error: t("visits.sidebar.micSync.abandonFailed"),
      });
    }
  };
  const visible = state?.lifetime === lifetime ? state : null;
  return {
    createLinkedRecordingSession,
    reset,
    abandon,
    retry: () =>
      saved?.kind === "pending"
        ? createLinkedRecordingSession(saved.deviceId)
        : Promise.resolve(),
    canRetry: saved?.kind === "pending",
    result: visible?.kind === "success" ? visible.result : null,
    loading: visible?.kind === "pending",
    error: storageError ?? (visible?.kind === "error" ? visible.error : null),
  };
};
